import { BaseEditor, Text } from "slate";
import { HistoryEditor } from "slate-history";
import { ReactEditor } from "slate-react";
import { BlockAddress } from "../output/types";

export type ResourceState = Record<string, LibraryResource>

export enum ResourceType {
  SCRIPTURE = 'Scripture',
  SONG = 'Song',
  LITURGY = 'Liturgy'
}

export enum LineType {
  NORMAL = 0,
  MINOR = 1
}

export type Line = [string, LineType, string?];

export type LineGroup = Line[];

export enum SongBlockType {
  VERSE = 'verse',
  PRECHORUS = 'prechorus',
  CHORUS = 'chorus',
  BRIDGE = 'bridge',
  TAG = 'tag',
  REFRAIN = 'refrain',
  ENDING = 'ending'
}

export type Block = {
  type: SongBlockType | LiturgyBlockType | ScriptureBlockType,
  content: LineGroup[]
};

export type SongBlock = Block & {
  type: SongBlockType
}

export type LicenceType = 'CCLI' | 'PUBLIC_DOMAIN' | 'EXPLICIT_PERMISSION' | 'OTHER'

export type SongMeta = {
  author?: string,
  copyright?: string,
  licence: LicenceType,
  created: number,
  modified: number,
  lastUsed?: number
}

export type SongResource = {
  id: string,
  title: string,
  type: ResourceType.SONG,
  content: Block[],
  meta: SongMeta
}

export enum LiturgyBlockType {
  SECTION = 'section'
}

export type LiturgyBlock = Block & {
  type: LiturgyBlockType
}

export type LiturgyMeta = {
  attribution?: string,
  created: number,
  modified: number,
  lastUsed?: number
}

export type LiturgyResource = {
  id: string,
  title: string,
  type: ResourceType.LITURGY,
  content: Block[],
  meta: LiturgyMeta
}

export enum ScriptureBlockType {
  SCRIPTURE = 'scripture'
}

export type ScriptureBlock = Block & {
  type: ScriptureBlockType
}

export type ScriptureMeta = {
  citation: string,
  copyright?: string,
  version: string
}

export type ScriptureResource = {
  id: string,
  title: string,
  type: ResourceType.SCRIPTURE,
  content: Block[],
  meta: ScriptureMeta
}

export type BlockType = SongBlockType | LiturgyBlockType | ScriptureBlockType;

export type Resource = SongResource | LiturgyResource | ScriptureResource;

export type LibraryResource = SongResource | LiturgyResource;

export type Meta = SongMeta | LiturgyMeta | ScriptureMeta;

export type NewResource = Omit<Resource, "id"> & { id?: string }

export type ResourceLocation = {
  block: number,
  group: number,
  line: number
}

export type ResourceSearchResult = {
  resource: LibraryResource,
  locations: ResourceLocation[]
}

export type AnnotatedResource = Resource & {
  annotations?: Record<string, unknown>
}

export const isResource = (thing: unknown): thing is Resource => {
  return (thing as Resource).id !== undefined;
};

export type LiveBlockAddress = BlockAddress & {
  wasClicked?: boolean
};

// Types for Slate editor

export type ResourceEditor = {
  resourceType: ResourceType | undefined,
  groupLines: () => void,
  setBlockType: (blockType: BlockType) => () => void,
  setBridge: () => void,
  setChorus: () => void,
  setLineStyle: (style: LineType) => () => void,
  setMajor: () => void,
  setMinor: () => void,
  setSection: () => void,
  setVerse: () => void,

  children: BlockElement[]
}

export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor & ResourceEditor;

export type BlockElement = {
  type: BlockType,
  typeIndex?: number,
  children: LineGroupElement[]
};

export type LineGroupElement = {
  type: 'lineGroup',
  children: LineElement[]
}

export type LineElement = {
  type: 'line',
  style: LineType,
  children: Text[]
}

export type CustomElement = BlockElement | LineGroupElement | LineElement;

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor
    Element: CustomElement
  }
}
